import mixpanel from 'mixpanel-browser';

const getEnvironment = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  
  const hostname = window.location.hostname;
  
  // Map domains to environments
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  if (hostname.includes('beta')) {
    return 'beta';
  }
  if (hostname.includes('usegreet.com')) {
    return 'production';
  }
  
  return hostname; // Return the actual domain if no match
};

// Check if we're in production (usegreet.com)
export const isProduction = (): boolean => {
  return getEnvironment() === 'production';
};

// Check if current user is an admin
export const isAdmin = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      return userData.isAdmin === true;
    }
  } catch {
    // Ignore errors parsing user data
  }
  return false;
};

export const initMixpanel = () => {
  // Only enable session recording in production and if user is not admin
  const shouldRecordSessions = isProduction() && !isAdmin();
  
  mixpanel.init('08a1ffc533823e46759a3092397d7932', {
    autocapture: true,
    record_sessions_percent: shouldRecordSessions ? 100 : 0,
    record_inline_images: false,
    record_mask_text_selector: '',
    record_heatmap_data: true,
    // todo set in the future to a couple seconds to ignore bounces
    // record_min_ms: number,
  });

  // Set environment as a super property (added to all events)
  const environment = getEnvironment();
  mixpanel.register({
    environment,
    domain: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
  });

  // Register UTM parameters as super properties so every downstream event carries attribution
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const utmProps: Record<string, string> = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
      const v = params.get(k);
      if (v) utmProps[k] = v;
    });
    if (Object.keys(utmProps).length) mixpanel.register(utmProps);
  }
};

export const identifyUser = (userId: string, userProperties: Record<string, any>) => {
  if (typeof window === 'undefined') return;
  
  try {
    mixpanel.identify(userId);
    mixpanel.people.set({
      '$name': userProperties.name || '',
      '$email': userProperties.email || '',
      ...userProperties,
    });
  } catch (error) {
    console.warn('Mixpanel identify failed:', error);
  }
};

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  // Check if mixpanel is initialized before tracking
  if (typeof window === 'undefined') return;
  
  try {
    mixpanel.track(eventName, properties);
  } catch (error) {
    console.warn('Mixpanel tracking failed:', error);
  }
};

export const resetUser = () => {
  if (typeof window === 'undefined') return;
  
  try {
    mixpanel.reset();
  } catch (error) {
    console.warn('Mixpanel reset failed:', error);
  }
};
